import React, { useState, useEffect } from "react";
import { useForm, useController, Controller, useFieldArray } from "react-hook-form";
import { useDispatch, useSelector } from 'react-redux';
import { updatePlan, createPlan, deletePlanVariant } from "../../slices/plans";
import { getPaymentProcessors } from "../../slices/paymentprocessors";
import { useNavigate } from 'react-router-dom';
import { SvgRecycleBin, SvgCancel } from "../svg";
import Select from '../select'
import useModal from "../../hooks/modal";
import Modal from "../../components/modal";


const translations = [
    gettext('Years'),
    gettext('Months'),
    gettext('Weeks'),
    gettext('Days'),
    gettext("Are you sure you want to delete this plan variant?"),
    gettext('Plan name'),
    gettext('Please add a name for this plan'),
    gettext('Alternative plan name (optional)'),
    gettext('Description'),
    gettext('Reference for other systems'),
    gettext('Day of renewal'),
    gettext('Use day of purchase'),
    gettext('Trial Period (in days)'),
    gettext('Payment processor'),
    gettext('Assign to plan group'),
    gettext('Recurring'),
    gettext("Determines whether plan will automatically renew or not."),
    gettext('Digital product?'),
    gettext("If checked, this is a digital product and the subscriber will not appear in distribution lists."),
    gettext('Enabled'),
    gettext("Can this variant accept new subscriptions"),
    gettext('Private'),
    gettext("Should the variant be publicly visible or not"),
    gettext('Renews every'),
    gettext('Please input a number'),
    gettext('Please input an interval'),
    gettext('Select interval'),
    gettext('Amount'),
    gettext('Please input an amount'),
    gettext('Please select a currency'),
    gettext('Select currency'),
    gettext('Language'),
    gettext('Please select a language'),
    gettext('Select language'),
    gettext('Plan variant identifier'),
    gettext("Add variant"),
    gettext("Save"),
    gettext('Close'),
    gettext('You cannot delete a planvariant that has active subscriptions'),
]


const PlanForm = ({ plan, planGroups }) => {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const currencies = useSelector((state) => state.currencies)
    const languages = useSelector((state) => state.plans.languages)
    const paymentProcessors = useSelector((state) => state.paymentprocessors.results)
    const [isShowingErrorModal, toggleErrorModal] = useModal();

    const { register, handleSubmit, control, reset, watch, setValue, touchedFields, formState: { errors, isDirty } } = useForm({
        defaultValues: {
            name: plan?.name,
            alternative_name: plan?.alternative_name,
            group: plan?.group?.id,
            description: plan?.description,
            reference: plan?.reference,
            variants: plan?.variants || [{}],
            recurring: plan?.recurring || true,
            electronic_only: plan?.electronic_only || true,
            billing_day_of_month: plan?.billing_day_of_month || 1,
            trial_period_days: plan?.trial_period_days || 10,
            enabled: plan?.enabled || false,
            private: plan?.private || false,
        }
    });

    const { field: group } = useController({ name: 'group', control: control })
    const { field: billing_day_of_month } = useController({ name: 'billing_day_of_month', control: control })
    const { field: payment_processor } = useController({ name: 'payment_processor', control: control })

    const intervalOptions = [
        { value: 'year', label: gettext('Years') },
        { value: 'month', label: gettext('Months') },
        { value: 'week', label: gettext('Weeks') },
        { value: 'day', label: gettext('Days') },
    ]

    useEffect(() => {
        dispatch(getPaymentProcessors())
    }, [])

    useEffect(() => {
        reset(plan)
    }, [plan])

    const onSubmit = (data) => {
        if (plan) {
            Promise.resolve(dispatch(updatePlan({ id: plan.id, args: data })))
                .then(data => {
                    navigate(`/plans/${plan.id}`)
                })
        } else {
            Promise.resolve(dispatch(createPlan(data)))
                .then(data => {
                    navigate(`/plans/${data.payload.id}`)
                })
        }
    }

    const { fields, append, remove } = useFieldArray({
        control,
        name: "variants",
        keyName: "key"
    });

    const getVariantError = (name, index) => {
        return errors['variants']?.[`${index}`]?.[name] ? <div className="error-msg text-left">{errors['variants'][`${index}`][name].message}</div> : null
    }

    const deleteVariant = (index, id) => {
        // if the ID is undefined then the variant has not been saved yet.
        if (id !== undefined) {
            if (confirm(gettext("Are you sure you want to delete this plan variant?"))) {
                dispatch(deletePlanVariant(id)).then((data) => {
                    if(data.payload.status == 'error'){
                        toggleErrorModal()
                    } else {
                        remove(index)
                    }
                })
            }
        }
        // otherwise, just remove it from the UI
        else {
            remove(index)
        }
    }

    return (
        <> 
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="contentarea clamped">
                <div className="container-fluid detail">
                    <div className="row">
                        <div className="col-xxl-8 col-12">
                            <div className="mb-md-4 mb-2">
                                <label>{gettext('Plan name')}</label>
                                <input type="text" {...register("name", { required: gettext('Please add a name for this plan') })} className="form-control" />
                                {errors['name'] ? <div className="error-msg text-left">{errors['name'].message}</div> : null}
                            </div>
                            <div className="mb-md-4 mb-2">
                                <label>{gettext('Alternative plan name (optional)')}</label>
                                <input type="text" {...register("alternative_name")} className="form-control" />
                            </div>
                            <div className="mb-md-4 mb-2">
                                <label>{gettext('Description')}</label>
                                <textarea {...register("description")} className="form-control" />
                            </div>
                            <div className="mb-md-4 mb-2">
                                <label>{gettext('Reference for other systems')}</label>
                                <input type="text" {...register("reference")} className="form-control" />
                            </div>
                            <div className="d-flex flex-xl-nowrap flex-wrap">
                                <div className="w-xl-50 w-100 mb-md-4 mb-2 me-xl-4">
                                    <label>{gettext('Day of renewal')}</label>
                                    <Select
                                        {...billing_day_of_month}
                                        options={[
                                            { label: gettext('Use day of purchase'), value: '' },
                                            ...Array.from(Array(28), (x, i) => {
                                                return { label: i + 1, value: i + 1 }
                                            })
                                        ]}
                                        isClearable
                                    />
                                </div>
                                <div className="w-xl-50 w-100 mb-md-4 mb-2">
                                    <label>{gettext('Trial Period (in days)')}</label>
                                    <input type="number" className="form-control" {...register("trial_period_days")} />
                                </div>
                            </div>
                            <div className="mb-md-4 mb-2">
                                <label>{gettext('Payment processor')}</label>
                                <Select
                                    {...payment_processor}
                                    options={paymentProcessors}
                                    getOptionLabel={(option) => `${option.name}`}
                                    getOptionValue={(option) => `${option.id}`}
                                />
                            </div>
                        </div>
                        <div className="col-xxl-4 col-12">
                            <div className="mb-xxl-5 mb-4">
                                <label>{gettext('Assign to plan group')}</label>
                                <Select
                                    {...group}
                                    options={planGroups}
                                    getOptionLabel={(option) => `${option.name}`}
                                    getOptionValue={(option) => `${option.id}`}
                                    isClearable
                                />
                            </div>
                            <div className="mb-xxl-5 mb-4">
                                <div className="form-check form-switch">
                                    <input id="recurring" className="form-check-input" type="checkbox" {...register("recurring")} role="switch" />
                                    <label htmlFor="recurring" className="form-check-label">{gettext('Recurring')}</label>
                                </div>
                                <small id="recurring-help" className="form-text text-muted">
                                    {gettext("Determines whether plan will automatically renew or not.")}
                                </small>
                            </div>
                            <div className="mb-xxl-5 mb-4">
                                <div className="form-check form-switch">
                                    <input id="electronic_only" className="form-check-input" type="checkbox" {...register("electronic_only")} role="switch" />
                                    <label htmlFor="electronic_only" className="form-check-label">{gettext('Digital product?')}</label>
                                </div>
                                <small id="electronic_only-help" className="form-text text-muted">
                                    {gettext("If checked, this is a digital product and the subscriber will not appear in distribution lists.")}
                                </small>
                            </div>
                            <div className="mb-xxl-5 mb-4">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" {...register(`enabled`)} type="checkbox" role="switch" id={`enabled`} />
                                    <label className="form-check-label" htmlFor={`enabled`}>{gettext('Enabled')}</label>
                                </div>
                                <small id="electronic_only-help" className="form-text text-muted">
                                    {gettext("Can this variant accept new subscriptions")}
                                </small>
                            </div>
                            <div className="mb-5">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" {...register(`private`)} type="checkbox" role="switch" id={`private`} />
                                    <label className="form-check-label" htmlFor={`private`}>{gettext('Private')}</label>
                                </div>
                                <small id="electronic_only-help" className="form-text text-muted">
                                    {gettext("Should the variant be publicly visible or not")}
                                </small>
                            </div>
                        </div>
                    </div>
                    {/* Plan variants */}
                    {fields.map((field, index) => (
                        <div key={`variant-${index + 1}`} className="row variants">
                            <div className="container-fluid mt-5 mb-5">
                                <h2>Áskriftarmöguleiki {index + 1}
                                    {Object.keys(fields).length > 1 &&
                                        <a className="float-end" type="button" onClick={() => deleteVariant(index, field.id)}><SvgRecycleBin /></a>
                                    }
                                </h2>
                                <div className="row">
                                    <div className="col-xxl-6 col-12 mb-4">
                                        <label>{gettext('Renews every')}</label>
                                        <div className="input-group">
                                            <input type="text"  {...register(`variants.${index}.interval_count`, { required: gettext('Please input a number') })} aria-label="First name" className="form-control" style={{ maxWidth: "30%", textAlign: 'center' }} />
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: gettext('Please input an interval'),
                                                }}
                                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                                    <>
                                                        <Select
                                                            className="react-select col"
                                                            options={intervalOptions}
                                                            onChange={(val) => onChange(val.value)}
                                                            onBlur={onBlur}
                                                            value={intervalOptions.find(function (option) {
                                                                return option.value === value
                                                            })}
                                                            placeholder={gettext('Select interval')}
                                                            innerRef={ref}
                                                            styles={{
                                                                control: (baseStyles) => ({
                                                                    ...baseStyles,
                                                                    borderTopLeftRadius: '0',
                                                                    borderBottomLeftRadius: '0',
                                                                }),
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                name={`variants.${index}.interval`}
                                            />
                                        </div>
                                        {getVariantError('interval_count', index)}
                                        {getVariantError('interval', index)}
                                    </div>
                                    <div className="col-xxl-6 col-12 mb-4">
                                        <label>{gettext('Amount')}</label>
                                        <div className="input-group">
                                            <input type="text" {...register(`variants.${index}.amount`, { required: gettext('Please input an amount') })} aria-label="First name" className="form-control" style={{ maxWidth: "30%", textAlign: 'center' }} />
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: gettext('Please select a currency'),
                                                }}
                                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                                    <Select
                                                        className="react-select col"
                                                        options={currencies}
                                                        onChange={(val) => onChange(val.code)}
                                                        onBlur={onBlur}
                                                        value={currencies.find(function (currency) {
                                                            return currency.code === value
                                                        })}
                                                        getOptionLabel={(option) => `${option.name}`}
                                                        getOptionValue={(option) => `${option.code}`}
                                                        placeholder={gettext('Select currency')}
                                                        innerRef={ref}
                                                        styles={{
                                                            control: (baseStyles) => ({
                                                                ...baseStyles,
                                                                borderTopLeftRadius: '0',
                                                                borderBottomLeftRadius: '0',
                                                            }),
                                                        }}
                                                    />
                                                )}
                                                name={`variants.${index}.currency`}
                                            />
                                        </div>
                                        {getVariantError('amount', index)}
                                        {getVariantError('currency', index)}
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xxl-4 col-12 mb-4">
                                        <label>{gettext('Language')}</label>
                                        <Controller
                                            control={control}
                                            rules={{
                                                required: gettext('Please select a language'),
                                            }}
                                            render={({ field: { onChange, onBlur, value, ref } }) => (
                                                <Select
                                                    options={languages}
                                                    onChange={(val) => onChange(val.code)}
                                                    onBlur={onBlur}
                                                    value={languages.find(function (language) {
                                                        return language.code === value
                                                    })}
                                                    getOptionLabel={(option) => `${option.name}`}
                                                    getOptionValue={(option) => `${option.code}`}
                                                    placeholder={gettext('Select language')}
                                                    innerRef={ref}
                                                />
                                            )}
                                            name={`variants.${index}.language`}
                                        />
                                        {getVariantError('language', index)}
                                    </div>
                                </div>
                                {field.id &&
                                    <div className="row">
                                        <div className="col-xxl-4 col-12">
                                            {gettext('Plan variant identifier')}: {field.id}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    ))}
                    <div className="row">
                        <div className="col mt-5">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => append()}>
                                {gettext("Add variant")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="clamped text-end mb-5 mb-md-0">
                <input className="btn btn-primary mt-4 ms-auto" type="submit" value={gettext("Save")} />
            </div>
        </form>
        <Modal
                show={isShowingErrorModal}
                onClose={toggleErrorModal}
                icon={<SvgCancel />}
                onClick={() => toggleErrorModal(false)}
                confirmButtonText={gettext('Close')}
                showCancelButton={false}
            >
                <p><strong>{gettext('You cannot delete a planvariant that has active subscriptions')}</strong></p>
            </Modal>
        </>
    )
}

export default PlanForm

